import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './index.css'
import App from './App.jsx'
import { ThemeProvider } from './context/ThemeContext.jsx'
import { queryClient } from './lib/queries/queryClient'
import { initSentry } from './lib/sentry'
import { initPosthog } from './lib/posthog'
import { AppErrorBoundary } from './components/AppErrorBoundary'

// Initialize Sentry before React mounts so the SDK is ready to capture any
// error during the initial render (bad import, crash in a Provider, etc.).
// No-op when VITE_SENTRY_DSN is unset — local dev and pre-signup builds
// run fine without it. See src/lib/sentry.js for the env-var contract.
initSentry()
// PostHog init for product analytics — same no-op-without-env-var contract.
// See src/lib/posthog.js. Must run before any capture() call, so it sits
// next to initSentry rather than inside a Provider.
initPosthog()

// ── Stale-chunk auto-reload ────────────────────────────────────────────────
//
// Symptom: a tab that booted on a previous deploy clicks a route that
// lazy-loads a now-missing chunk. Netlify's SPA fallback returns
// /index.html for the unknown asset path, the browser tries to parse HTML
// as a JS module, and the user lands on the AppErrorBoundary's fallback
// with a Sentry reference and no idea what to do.
//
// Fix: catch the chunk-load failure BEFORE the boundary, force one reload
// to pick up the new index.html (with new bundle hashes), and stop. The
// 30s sessionStorage guard prevents an infinite loop if the post-reload
// page somehow hits the same failure (legitimate broken deploy vs. stale
// chunk — we don't want to keep reloading either way).
const CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to load module script/i,
  /Importing a module script failed/i,
  /Expected a JavaScript-or-Wasm module script/i,
]
function isChunkLoadError(err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = String(err.message || err)
  return CHUNK_PATTERNS.some(re => re.test(msg))
}
function reloadOnceForStaleChunk() {
  try {
    const last = Number(sessionStorage.getItem('planos.chunk_reload_at') || 0)
    if (Date.now() - last < 30_000) return false   // already tried — give up
    sessionStorage.setItem('planos.chunk_reload_at', String(Date.now()))
  } catch { /* sessionStorage blocked — try anyway */ }
  window.location.reload()
  return true
}
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason)) reloadOnceForStaleChunk()
})
window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.error || e.message)) reloadOnceForStaleChunk()
})

// AppErrorBoundary sits OUTSIDE ThemeProvider so a theme-layer crash still
// renders a usable fallback. The boundary reports to Sentry automatically.
//
// QueryClientProvider sits INSIDE the boundary so a query-layer crash is
// recoverable; OUTSIDE ThemeProvider so theme changes don't blow the cache.
// Devtools mount in dev only — vite tree-shakes the import out of prod
// builds when import.meta.env.DEV is false at the call site.
createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <App />
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
)

// ── Service worker registration ────────────────────────────────────────────
//
// Enables offline mode for the vendor day-of portal (and is harmless for the
// rest of the app: shell is cached, API calls pass through untouched). Skip
// in dev so Vite HMR isn't interrupted.
//
// Keeping beta testers off stale bundles is trickier than registration alone:
//   1. A tab that booted on bundle `index-OLD.js` keeps running old code
//      even after a new deploy — the SW happily serves new hashed bundles
//      to NEW requests, but nothing triggers a reload.
//   2. Browsers revalidate /sw.js only every ~24h by default, so a fresh
//      sw.js sitting on Netlify can go unnoticed for a day.
//
// Fix pattern (below):
//   • On a fresh SW activation, sw.js posts `SW_ACTIVATED` to all open
//     windows. We reload once per tab (guarded by `reloading`).
//   • On tab focus / visibility, we call `registration.update()` (throttled
//     to 15 min) so the browser checks sw.js more aggressively than the
//     default 24h. Combined with `skipWaiting` + `clients.claim` in sw.js,
//     this gets new builds to every open tab within one focus cycle.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000
  let lastUpdateCheck = 0
  let reloading = false

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Auto-reload when a new SW takes control and posts that it activated.
        // Scoped to the `reloading` flag so two near-simultaneous messages
        // (activate from SW + controllerchange on registration) don't race
        // into a double-reload.
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_ACTIVATED' && !reloading) {
            reloading = true
            window.location.reload()
          }
        })

        // Throttled update-probe on tab focus/visibility. Without this the
        // browser waits up to 24h before re-checking /sw.js — beta testers
        // keeping the app pinned would stay on last night's build all day.
        const maybeCheck = () => {
          if (Date.now() - lastUpdateCheck < UPDATE_CHECK_INTERVAL_MS) return
          lastUpdateCheck = Date.now()
          registration.update().catch(() => {})
        }
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') maybeCheck()
        })
        window.addEventListener('focus', maybeCheck)
      })
      .catch((err) => console.warn('[sw] registration failed:', err))
  })
}
